import { Component, Inject, ViewChild, OnDestroy } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatDialog,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { MatDialogRef } from '@angular/material/dialog';
import { Subscription, throwError, catchError, of, switchMap, map } from 'rxjs';
import { OrderDetails, PaymentType, OrderCancellationReason } from '@interfaces/orders/1.0/index';
import { OrdersService } from '@services/orders/1.0/orders.service';
import { LoadingColor, LoadingType } from '@constants/loading/1.0';
import { BadgeType } from '@constants/badge/1.0';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { MessageType } from '@constants/message/1.0';
import { DeepCloneService } from '@services/utilities/1.0/deep-clone.service';
import { ConfirmationComponent } from '@components/confirmation/1.0';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { CustomerFacingReasons } from '@interfaces/orders/1.0/index';
import { PermissionsService } from '@services/permissions/1.0/permissions.service';
import { StoreService } from '@services/store/1.0/store.service';
import { DateService } from '@services/utilities/1.0/date.service';
import { UtilitiesService } from '@services/utilities/1.0/utilities.service';
import { OrderPdfComponent } from '@app/orders/orders-dashboard/order-pdf/order-pdf.component';
import { OrderProcessingStatus } from '@generated/tcea/models/order-processing-status';
import { CancelOrderDialogComponent } from '@components/cancel-order-dialog/cancel-order-dialog.component';
import { CANCELABLE_AGGREGATOR_CHANNELS } from '@constants/orders/1.0';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { CdkScrollable } from '@angular/cdk/scrolling';
import {
  NgIf,
  NgFor,
  NgClass,
  NgTemplateOutlet,
  DecimalPipe,
  DatePipe,
  KeyValuePipe,
  TitleCasePipe,
} from '@angular/common';
import { LoadingComponent } from '../loading/1.0/loading.component';
import { MessageComponent } from '../message/1.0/message.component';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { ButtonComponent } from '../button/1.0/button.component';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { NumbersOnlyDirective } from '../../directives/numbers-only/1.0/numbers-only.directive';
import { BadgeComponent } from '../badge/1.0/badge.component';
import { OrderPdfComponent as OrderPdfComponent_1 } from '../../../app/orders/orders-dashboard/order-pdf/order-pdf.component';

@Component({
  selector: 'txp-order-details-dialog',
  templateUrl: './order-details-dialog.component.html',
  styleUrls: ['./order-details-dialog.component.scss'],
  standalone: true,
  imports: [
    TitleCasePipe,
    MatDialogTitle,
    MatIconButton,
    MatTooltip,
    CdkScrollable,
    MatDialogContent,
    NgIf,
    LoadingComponent,
    MessageComponent,
    MatTabGroup,
    MatTab,
    NgFor,
    ButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    NumbersOnlyDirective,
    MatError,
    NgClass,
    BadgeComponent,
    NgTemplateOutlet,
    MatDialogActions,
    OrderPdfComponent_1,
    TranslateModule,
    DecimalPipe,
    DatePipe,
    KeyValuePipe,
  ],
})
export class OrderDetailsDialogComponent implements OnDestroy {
  loadingType = LoadingType; // expose enum to template
  buttonStyle = ButtonStyle; // expose enum to template
  buttonSize = ButtonSize; // expose enum to template
  loadingColor = LoadingColor; // expose enum to template
  messageType = MessageType; // expose enum to template
  badgeType = BadgeType; // expose enum to template
  customerFacingReasons = CustomerFacingReasons;

  emptyOrdersMsg: boolean;
  failedNegateMsg = false;
  paymentForms = new Map<string, UntypedFormGroup>(); // Map of transactionId to form
  paymentRefundModes = new Map<string, boolean>(); // Map of transactionId to refund mode
  paymentRefundingStates = new Map<string, boolean>(); // Map of transactionId to refunding state
  paymentRefundErrors = new Map<string, boolean>(); // Map of transactionId to refund error state
  orderDetails: OrderDetails;
  hasOrderDetails = false;
  isLoading: boolean;
  subscription: Subscription;
  showError = false;
  showLoadError = false;
  loadErrorMessageKey = 'orders.grid.loadFailMsg';
  showPrintError = false;
  showMoveError = false;
  isMoving = false;
  canMoveToNextStatus = false;
  groupedItems: any;
  orderProcessingStatus = OrderProcessingStatus;
  canWriteOrders = false;

  negationStatus: undefined | 'loading' | 'refunded' | 'cancelled';
  isCancellingOrder = false;
  private cancelDialogRef: MatDialogRef<CancelOrderDialogComponent> | null = null;

  @ViewChild(OrderPdfComponent) ordersPdfComponent!: OrderPdfComponent;

  constructor(
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<OrderDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { orderId: string; currency: string },
    private formBuilder: UntypedFormBuilder,
    private ordersService: OrdersService,
    private storeService: StoreService,
    private permissionsService: PermissionsService,
    private translateService: TranslateService,
    private dateService: DateService,
    private utilitiesService: UtilitiesService,
  ) {
    this.canWriteOrders = this.permissionsService.getPermission('order:write' as any);
    this.getOrderDetails(this.data.orderId);
  }

  ngOnDestroy(): void {
    this.cancelDialogRef?.close();
  }

  dismissError() {
    this.showLoadError = false;
    this.loadErrorMessageKey = 'orders.grid.loadFailMsg';
    this.emptyOrdersMsg = false;
    this.failedNegateMsg = false;
    this.paymentRefundErrors.clear();
    this.showPrintError = false;
    this.showMoveError = false;
  }

  /**
   * Get all orders and populate the data source as the data is observed
   **/
  getOrderDetails(orderId: string) {
    this.emptyOrdersMsg = false;
    if (orderId && orderId.length) {
      this.orderDetails = undefined;
      this.isLoading = true;
      let orderDetailsError = false;
      let orderRefundsError = false;

      this.subscription = this.ordersService
        .getOrderById(orderId)
        .pipe(
          catchError(() => {
            orderDetailsError = true;
            return of(null);
          }),
          switchMap((details) => {
            if (details) {
              if (details.hasRefunds === false) {
                return of([details, []] as [OrderDetails | null, any[]]);
              }
              return this.ordersService.getOrderRefunds(orderId).pipe(
                catchError(() => {
                  orderRefundsError = true;
                  return of([]);
                }),
                map((refunds) => [details, refunds] as [OrderDetails | null, any[]]),
              );
            } else {
              return this.ordersService.getOrderRefunds(orderId).pipe(
                catchError(() => {
                  orderRefundsError = true;
                  return of([]);
                }),
                map((refunds) => [null, refunds] as [OrderDetails | null, any[]]),
              );
            }
          }),
        )
        .subscribe({
          next: ([details, refunds]) => {
            if (orderDetailsError && orderRefundsError) {
              this.showError = true;
              this.showLoadError = true;
              this.loadErrorMessageKey = 'orders.grid.loadFailMsg';
            } else if (orderRefundsError) {
              this.showError = true;
              this.showLoadError = true;
              this.loadErrorMessageKey = 'orders.orderInfo.refundsLoadFailMsg';
            } else if (orderDetailsError) {
              this.showError = true;
              this.showLoadError = true;
              this.loadErrorMessageKey = 'orders.orderInfo.detailsLoadFailMsg';
            } else {
              this.showLoadError = false;
            }
            if (details) {
              this.hasOrderDetails = true;
              this.orderDetails = DeepCloneService.clone(details);
              this.orderDetails.refunds = DeepCloneService.clone(refunds || []).sort((a, b) => {
                const dateA = new Date(a.timestamp);
                const dateB = new Date(b.timestamp);
                return dateA.getTime() - dateB.getTime();
              });
              this.orderDetails.orderDate = details.orderDate
                ? this.dateService.formatUTCToLocalTime(details.orderDate)
                : undefined;
              const estTime =
                details.fulfillmentInfo?.fulfillmentDateTime ??
                details.fulfillmentInfo?.estimatedTime ??
                details.estimatedTime;
              this.orderDetails.estimatedTime = estTime
                ? this.dateService.formatUTCToLocalTime(estTime)
                : undefined;
              this.orderDetails.paymentType = PaymentType[details.paymentType];
              this.updateCanMoveToNextStatus();
            } else {
              this.hasOrderDetails = false;
              const sortedRefunds = DeepCloneService.clone(refunds || []).sort((a, b) => {
                const dateA = new Date(a.timestamp);
                const dateB = new Date(b.timestamp);
                return dateA.getTime() - dateB.getTime();
              });
              this.orderDetails = {
                refunds: sortedRefunds,
                hasRefunds: sortedRefunds.length > 0,
              } as any;
            }
            this.isLoading = false;
          },
          error: (err) => {
            throwError(err);
            this.showError = true;
            this.showLoadError = true;
            this.isLoading = false;
          },
          complete: () => {
            if (this.orderDetails) {
              this.groupItemsByGuestName();
              this.initializePaymentForms();
            }
            this.isLoading = false;
          },
        });
    }
  }

  groupItemsByGuestName() {
    if (this.orderDetails.items) {
      this.groupedItems = this.orderDetails.items.reduce((grouped, item) => {
        const key = item.guestName || 'default';
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(item);
        return grouped;
      }, {});
    }
  }

  calculateSubtotal(items: any[]): number {
    return items.reduce((sum, item) => sum + item.price, 0);
  }

  trackByTransactionId(index: number, payment: any): string {
    return payment.transactionId;
  }

  formatOrderProcessingStatus(status: string): string {
    if (!status) {
      return 'N/A';
    }

    const normalized = String(status).trim();
    if (!normalized) {
      return 'N/A';
    }

    let spacedStatus = normalized
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();

    const compactLower = spacedStatus.replace(/\s+/g, '').toLowerCase();
    if (compactLower === 'processcancellation' || compactLower === 'processingcancellation') {
      return 'Process Cancellation';
    }

    return spacedStatus
      .split(' ')
      .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`)
      .join(' ');
  }

  initializePaymentForms() {
    if (this.orderDetails?.paymentDetails?.payments) {
      this.orderDetails.paymentDetails.payments.forEach((payment) => {
        if (payment.transactionId) {
          const form = this.formBuilder.group({
            amount: new UntypedFormControl(''),
            description: new UntypedFormControl(''),
            reason: new UntypedFormControl(''),
          });
          this.paymentForms.set(payment.transactionId, form);
          this.paymentRefundModes.set(payment.transactionId, false);
          this.paymentRefundingStates.set(payment.transactionId, false);
          this.paymentRefundErrors.set(payment.transactionId, false);
        }
      });
    }
  }

  getPaymentRefunds(transactionId: string): any[] {
    return (this.orderDetails?.refunds || []).filter(
      (refund) => refund.transactionId === transactionId,
    );
  }

  maxRefundValueForPayment(payment: any): number {
    const paymentRefunds = this.getPaymentRefunds(payment.transactionId);
    const amountRefunded =
      paymentRefunds.reduce((acc, refund: any) => {
        if (refund.status !== 'SUCCEEDED') {
          return acc;
        }
        return acc + parseFloat(refund.amount);
      }, 0) || 0;

    return parseFloat(payment.amount) - amountRefunded;
  }

  getRemainingAmountForPayment(payment: any): number {
    const form = this.paymentForms.get(payment.transactionId);
    if (!form) return this.maxRefundValueForPayment(payment);

    const currentInputAmount = parseFloat(form.get('amount')?.value || '0') || 0;
    const maxRefundable = this.maxRefundValueForPayment(payment);

    return Math.max(0, maxRefundable - currentInputAmount);
  }

  shouldShowRemainingAmount(payment: any): boolean {
    const form = this.paymentForms.get(payment.transactionId);
    if (!form) return false;

    const amountControl = form.get('amount');
    const hasErrors = amountControl?.errors && amountControl?.touched;
    const hasValue = amountControl?.value && parseFloat(amountControl.value) > 0;

    return !hasErrors && hasValue;
  }

  refundEnabledForPayment(payment: any): boolean {
    const { orderProcessingStatus } = (this.orderDetails || {}) as any;
    const userCanRefund = this.permissionsService.getPermission('order.refunds:write');
    const isCompleted = orderProcessingStatus?.toUpperCase() === 'COMPLETED';
    const isPaidStatus = payment.status === 'Paid' || payment.status === 'Authorized';

    const refundAllowedByPaymentAdapter = () => {
      let isChannelRefundAllowed = this.orderDetails?.channelName === 'CONSUMER-API';
      if (isChannelRefundAllowed) {
        this.storeService.getOne(this.orderDetails.store.storeId).subscribe({
          next: (store) => {
            isChannelRefundAllowed = store?.paymentAdapterList[0]?.allowRefund || false;
            return isChannelRefundAllowed;
          },
        });
      }
      return isChannelRefundAllowed;
    };

    return (
      userCanRefund &&
      refundAllowedByPaymentAdapter &&
      isCompleted &&
      isPaidStatus &&
      this.maxRefundValueForPayment(payment) > 0
    );
  }

  toggleRefundForPayment(transactionId: string) {
    const form = this.paymentForms.get(transactionId);
    const currentMode = this.paymentRefundModes.get(transactionId) || false;
    const payment = this.orderDetails?.paymentDetails?.payments?.find(
      (p) => p.transactionId === transactionId,
    );

    if (!form || !payment) return;

    const amountControl = form.get('amount');
    const newMode = !currentMode;

    this.paymentRefundModes.set(transactionId, newMode);

    if (newMode) {
      amountControl.setValidators([
        Validators.required,
        Validators.max(this.maxRefundValueForPayment(payment)),
      ]);
    } else {
      amountControl.clearValidators();
      form.reset({});
    }
    amountControl.updateValueAndValidity();
  }

  handleRefundForPayment(transactionId: string) {
    const form = this.paymentForms.get(transactionId);
    const payment = this.orderDetails?.paymentDetails?.payments?.find(
      (p) => p.transactionId === transactionId,
    );

    if (!form || !payment) return;

    form.markAllAsTouched();

    if (!form.valid) {
      return;
    }

    const { tenantId, store } = this.orderDetails;
    const amount = parseFloat(form.get('amount').value);
    const formData = DeepCloneService.clone(form.value);
    const refundData = {
      ...formData,
      amount,
      tenantId,
      storeId: store.storeId,
      orderId: this.data.orderId,
      currency: payment.currency,
      transactionId: transactionId,
    };

    this.paymentRefundingStates.set(transactionId, true);

    this.ordersService.postOrderRefund(transactionId, refundData).subscribe({
      next: (response) => {
        this.orderDetails.refunds = [...(this.orderDetails.refunds || []), response];
        this.paymentRefundingStates.set(transactionId, false);
        form.reset({});
        this.paymentRefundModes.set(transactionId, false);
      },
      error: () => {
        form.reset({});
        this.paymentRefundingStates.set(transactionId, false);
        this.paymentRefundErrors.set(transactionId, true);
      },
    });
  }

  maxRefundValue() {
    if (this.orderDetails?.paymentDetails?.payments?.[0]) {
      return this.maxRefundValueForPayment(this.orderDetails.paymentDetails.payments[0]);
    }

    const amountRefunded =
      this.orderDetails?.refunds?.reduce((acc, refund: any) => {
        if (refund.status !== 'SUCCEEDED') {
          return acc;
        }

        return acc + parseFloat(refund.amount);
      }, 0) || 0;

    return parseFloat(this.orderDetails?.channelTotal) - amountRefunded;
  }

  refundEnabled() {
    const { orderProcessingStatus, paymentAttempts } = (this.orderDetails || {}) as any;
    const userCanRefund = this.permissionsService.getPermission('tenant:write');
    const hasSuccessfulPayment = !!(paymentAttempts || []).find((payment) => {
      return payment.status === 'Success';
    });
    const isCompleted = orderProcessingStatus?.toUpperCase() === 'COMPLETED';

    const refundAllowedByPaymentAdapter = () => {
      let isChannelRefundAllowed = this.orderDetails?.channelName === 'CONSUMER-API';
      if (isChannelRefundAllowed) {
        this.storeService.getOne(this.orderDetails.store.storeId).subscribe({
          next: (store) => {
            isChannelRefundAllowed = store?.paymentAdapterList[0]?.allowRefund || false;
            return isChannelRefundAllowed;
          },
        });
      }
      return isChannelRefundAllowed;
    };

    return (
      userCanRefund &&
      refundAllowedByPaymentAdapter &&
      isCompleted &&
      hasSuccessfulPayment &&
      this.maxRefundValue() > 0
    );
  }

  handleNegateOrder() {
    this.negationStatus = 'loading';

    const modalData = {
      title: this.translateService.instant('orders.orderInfo.negateOrder.title'),
      message: this.translateService.instant('orders.orderInfo.negateOrder.message'),
      cancelText: this.translateService.instant('form.button.cancel'),
      confirmText: this.translateService.instant('form.button.continue'),
    };
    const confirmationDialogRef = this.dialog.open(ConfirmationComponent, { data: modalData });
    confirmationDialogRef.afterClosed().subscribe({
      next: (confirmAction: boolean) => {
        if (!confirmAction) {
          this.negationStatus = undefined;
          return;
        }

        const { hasRefunds, isCancelable, tenantId } = this.orderDetails;
        if (isCancelable) {
          this.ordersService.postOrderCancel(tenantId, this.data.orderId).subscribe({
            next: () => (this.negationStatus = 'cancelled'),
            error: () => (this.failedNegateMsg = true),
          });
        } else if (!hasRefunds) {
          this.ordersService.postOrderRefund(this.data.orderId, {}).subscribe({
            next: () => (this.negationStatus = 'refunded'),
            error: () => (this.failedNegateMsg = true),
          });
        }
      },
    });
  }

  handleCancelOrder() {
    if (!this.canWriteOrders) {
      return;
    }

    const hasCancelableAggregatorOrder = this.isAggregatorCancelableChannel(
      this.orderDetails?.channelName,
    );
    // Determine if this is a CXA order and delivery order
    const isCxaOrder =
      !hasCancelableAggregatorOrder && this.orderDetails?.channelName === 'CONSUMER-API';
    const serviceType =
      this.orderDetails?.fulfillmentInfo?.serviceType ?? this.orderDetails?.serviceType;
    const isDeliveryOrder =
      serviceType === 'DeliveryByChannel' || serviceType === 'DeliveryByRestaurant';

    const dialogData = {
      orderIds: [this.data.orderId],
      isCxaOrder,
      isDeliveryOrder,
      isBulk: false,
      onConfirm: (cancellationReason: OrderCancellationReason) => {
        // Handle the API call
        this.ordersService.cancelOrder(this.data.orderId, cancellationReason).subscribe({
          next: () => {
            // Update order status locally
            this.orderDetails.orderProcessingStatus = OrderProcessingStatus.Canceled;
            this.orderDetails.cancellationReason = cancellationReason;

            // Close the confirmation dialog
            this.cancelDialogRef?.close();

            // Close the order details dialog and show success message
            this.dialogRef.close({
              success: true,
              message: this.translateService.instant('orders.orderInfo.cancelOrderSuccessMsg'),
              orderId: this.data.orderId,
            });
          },
          error: () => {
            // Show error in the confirmation dialog
            if (this.cancelDialogRef?.componentInstance) {
              this.cancelDialogRef.componentInstance.showError = true;
              // Reset loading state in the confirmation dialog
              this.cancelDialogRef.componentInstance.isLoading = false;
            }
          },
        });
      },
    };

    this.cancelDialogRef = this.dialog.open(CancelOrderDialogComponent, {
      data: dialogData,
      hasBackdrop: false,
      panelClass: 'nested-dialog',
    });

    // Handle when the dialog is closed without confirmation
    this.cancelDialogRef.afterClosed().subscribe((result) => {
      // If the dialog was closed without a result (clicked outside, pressed escape, etc.)
      // we don't need to do anything - the user cancelled the operation
    });
  }

  isOrderUiCancelable(): boolean {
    return Boolean(
      this.canWriteOrders &&
        (this.orderDetails?.isCancelable ||
          this.isAggregatorCancelableChannel(this.orderDetails?.channelName)),
    );
  }

  private isAggregatorCancelableChannel(channelName?: string): boolean {
    return CANCELABLE_AGGREGATOR_CHANNELS.includes(channelName || '');
  }

  handleClose() {
    this.dialogRef.close();
  }

  printOrder() {
    if (!this.hasOrderDetails || !this.orderDetails) return;
    this.showPrintError = false;
    this.isLoading = true;

    this.ordersPdfComponent
      .generatePdf(this.orderDetails, this.data.currency)
      .pipe(
        catchError(() => {
          this.showPrintError = true;
          return of(null);
        }),
      )
      .subscribe(() => {
        this.isLoading = false;
      });
  }

  getNextStatus(): OrderProcessingStatus | null {
    if (!this.orderDetails?.orderProcessingStatus) return null;

    const currentStatus = this.orderDetails.orderProcessingStatus;

    // Don't allow moves for final states
    if (
      currentStatus === OrderProcessingStatus.Completed ||
      currentStatus === OrderProcessingStatus.Failed ||
      currentStatus === OrderProcessingStatus.Canceled
    ) {
      return null;
    }

    switch (currentStatus) {
      case OrderProcessingStatus.Placed:
        return OrderProcessingStatus.Kitchen;
      case OrderProcessingStatus.Kitchen:
        return OrderProcessingStatus.Ready;
      case OrderProcessingStatus.Ready:
        return OrderProcessingStatus.Completed;
      default:
        return null;
    }
  }

  getNextStatusDisplayName(): string {
    const nextStatus = this.getNextStatus();
    if (!nextStatus) return '';

    switch (nextStatus) {
      case OrderProcessingStatus.Kitchen:
        return `${this.translateService.instant('orders.dashboard.moveTo')} ${this.translateService.instant('orders.dashboard.boards.in_kitchen')}`;
      case OrderProcessingStatus.Ready:
        return `${this.translateService.instant('orders.dashboard.moveTo')} ${this.translateService.instant('orders.dashboard.boards.ready')}`;
      case OrderProcessingStatus.Completed:
        return `${this.translateService.instant('orders.dashboard.moveTo')} ${this.translateService.instant('orders.dashboard.boards.complete')}`;
      default:
        return '';
    }
  }

  updateCanMoveToNextStatus() {
    this.canMoveToNextStatus = this.canMoveToNextStatusCheck();
  }

  private canMoveToNextStatusCheck(): boolean {
    if (!this.canWriteOrders) return false;
    if (!this.orderDetails?.orderProcessingStatus) return false;

    const currentStatus = this.orderDetails.orderProcessingStatus;

    // Don't show button for final states
    if (
      currentStatus === OrderProcessingStatus.Completed ||
      currentStatus === OrderProcessingStatus.Failed ||
      currentStatus === OrderProcessingStatus.Canceled
    ) {
      return false;
    }

    // Only show for orders that can be moved forward
    const allowedStatuses = [
      OrderProcessingStatus.Placed,
      OrderProcessingStatus.Kitchen,
      OrderProcessingStatus.Ready,
    ];
    const hasValidStatus = allowedStatuses.includes(currentStatus);

    const nextStatus = this.getNextStatus();
    return hasValidStatus && nextStatus !== null;
  }

  moveToNextStatus() {
    if (!this.canWriteOrders) return;

    const nextStatus = this.getNextStatus();
    if (!nextStatus || !this.orderDetails) return;

    this.showMoveError = false;
    this.isMoving = true;
    this.updateCanMoveToNextStatus();

    this.ordersService
      .putOrderProcessingStatus(this.orderDetails.orderId, nextStatus)
      .pipe(
        catchError(() => {
          this.showMoveError = true;
          return of(null);
        }),
      )
      .subscribe((response) => {
        this.isMoving = false;
        if (response !== null) {
          // Update the order status locally
          this.orderDetails.orderProcessingStatus = nextStatus;
          this.updateCanMoveToNextStatus();

          // Close dialog after any successful status change
          this.handleClose();
        } else {
          this.updateCanMoveToNextStatus();
        }
      });
  }

  /**
   * Translate device type enum with fallback to original string
   */
  translateDeviceType(deviceType: string): string {
    return this.utilitiesService.translateDeviceType(deviceType);
  }

  /**
   * Translate ordering channel enum with fallback to original string
   */
  translateOrderingChannel(orderingChannel: string): string {
    return this.utilitiesService.translateOrderingChannel(orderingChannel);
  }
}
